// src/components/plan/PlanSession.tsx
//
// "Plan October": Look back at September · Plan October · Save — the level
// above beside you the whole time (spec: guided planning, Phase 1). A pure
// view over a SessionDraft; the page owns loading, the draft store and Save.
// The page hands in the PRUNED draft (pruneDraft), the same one it saves, so
// the summary here is exactly what Save writes.

import { useMemo, useState } from 'react'
import { Target, Check } from 'lucide-react'
import type { Task } from '@/types/task'
import { DOMAINS, type DomainId } from '@/lib/domains'
import { verdictOptions, summarize, weekTaskListLabel, placementLevelOf, type SessionDraft, type SessionLevel, type Verdict } from '@/lib/planning/session'
import { goalListView, planRowsFor, hiddenLabel, countsLabel, clearFilterOnEscape } from '@/lib/planning/goalListView'
import { stepsThatCarryForward } from '@/lib/planning/goalSteps'
import { Hint } from './Hint'

type Step = 'back' | 'plan' | 'save'
/** The row's REAL id, fixed when it is written into the draft: creating it twice finds the first (idempotent insert, Task 0). */
const newId = () => crypto.randomUUID()

export function PlanSession({ level, aboveLabel, dayOptions = [], periodLabel: P, prevLabel: Q, finished, open, current, above, aboveGoals, hiddenStepGoals, domainInView = null, uid = null, draft, onChange, onClose, onSave, saving, saveError }: {
  /** The level being planned. The week plans tasks only, and may name a day. */
  level: SessionLevel
  /** The level above, as this session names it: 'the season' for a month, 'October' for a week. */
  aboveLabel: string
  /** Week only: the seven days this week offers a task. */
  dayOptions?: Array<{ ymd: string; label: string }>
  periodLabel: string; prevLabel: string
  finished: Task[]; open: Task[]; current: Task[]; above: Task[]; aboveGoals: Task[]
  /** Goals whose open steps this view hides — a Keep carries them too, and the summary says so. */
  hiddenStepGoals?: ReadonlySet<string>
  /** The domain in view — a new item is created in it (recorded as it is added, not at Save). */
  domainInView?: DomainId | null
  /** The signed-in person, for the first-time hints' per-user storage key. */
  uid?: string | null
  draft: SessionDraft; onChange: (d: SessionDraft) => void
  onClose: () => void; onSave: () => Promise<void>; saving: boolean; saveError?: boolean
}) {
  const needsDomain = [...open.filter(task => draft.verdicts[task.id] === 'someday'), ...above.filter(task => draft.takenFromAbove.includes(task.id))].filter(task => !task.context && !task.parentTaskId)
  const missingDomains = needsDomain.filter(task => !draft.domains?.[task.id])
  const nothingBack = finished.length === 0 && open.length === 0
  const [step, setStep] = useState<Step>(nothingBack ? 'plan' : 'back')
  const [goalText, setGoalText] = useState('')
  const [goalFor, setGoalFor] = useState('')
  const [taskText, setTaskText] = useState('')
  const [taskToward, setTaskToward] = useState('')
  const [taskDay, setTaskDay] = useState('')
  // Month and season plan in outcomes; the single-action box is the second
  // route, one press away (horizon flows, 2026-09-26). The week IS actions.
  const [addingTask, setAddingTask] = useState(false)
  const week = level === 'week'
  const season = level === 'season'
  const year = level === 'year'
  const copy = {
    backSub: week
      ? `This is ${Q}'s actual list. Keep what still matters; a ticked row is already done.`
      : year
      ? `This is ${Q}'s goals. Keep what still matters into ${P}; Done marks it finished; Drop archives it.`
      : `This is ${Q}'s actual list. A task here is new work toward the goal; the goal itself stays.`,
    finished: week ? `Finished ${Q}` : `Finished in ${Q}`,
    planHead: week ? `What will you get done ${P}?` : year ? `What will ${P} add up to?` : `What will ${P} add up to, and what will you do?`,
    planSub: week
      ? `Look at ${aboveLabel} beside you. Add what ${P} can take; most things don't need a day.`
      : year
      ? 'Goals only. Seasons and months plan the work.'
      : season
      ? `Write ${P}'s goals and projects with ${aboveLabel} beside you. Goals stay on the season; their tasks are planned into weeks.`
      : `Write ${P}'s goals and projects with the season beside you. Goals stay on the month; their tasks are planned into weeks.`,
    saveHead: week ? 'Here\'s the week' : year ? `Here's ${P}` : `Here's ${P}'s plan`,
    taskHead: week ? weekTaskListLabel(P) : 'Tasks',
    marker: week ? `· on ${P}` : `· in ${P}`,
    aboveEmpty: season ? `${aboveLabel} has no goals yet.` : `${aboveLabel} has no list yet.`,
    goalForWord: season ? aboveLabel : 'season',
  }
  // "Keep, and add a task" with no action named would save as a plain
  // Keep and silently lose the action — so the session will not move on
  // until the row is named or switched to Keep.
  const unnamedActions = week ? [] : open.filter((t) => draft.verdicts[t.id] === 'keep-action' && !draft.actionTitles[t.id]?.trim()).map((t) => t.id)
  const [askForActions, setAskForActions] = useState(false)
  const goTo = (next: Step) => {
    if (next !== 'back' && unnamedActions.length > 0) { setAskForActions(true); setStep('back'); return }
    setAskForActions(false)
    setStep(next)
  }
  const set = (patch: Partial<SessionDraft>) => onChange({ ...draft, ...patch })

  const setVerdict = (id: string, v: Verdict) => {
    const verdicts = { ...draft.verdicts }
    if (verdicts[id] === v) delete verdicts[id]; else verdicts[id] = v
    const patch: Partial<SessionDraft> = { verdicts }
    // A year Keep needs the id the kept copy will be created with, fixed once
    // so a retried Save re-uses it (Task 2/4). A toggle off or away leaves a
    // stale id in place — harmless, and keeps a later retry idempotent.
    if (level === 'year' && verdicts[id] === 'keep' && !draft.keptIds?.[id]) {
      patch.keptIds = { ...(draft.keptIds ?? {}), [id]: newId() }
    }
    set(patch)
  }

  // What this month holds in the draft, for the Plan step and the "toward" options.
  const isKept = (id: string) => draft.verdicts[id] === 'keep' || draft.verdicts[id] === 'keep-action'
  const kept = open.filter((t) => isKept(t.id))
  // A kept goal's open steps travel with it (keepForward) unless they have a verdict of their own.
  const carried = kept.filter((g) => g.isGoal).flatMap((g) => stepsThatCarryForward(g.id, open, placementLevelOf(level))).filter((st) => !draft.verdicts[st.id])
  const monthGoals = week ? [] : [...current.filter((t) => t.isGoal), ...kept.filter((t) => t.isGoal)]
  const monthTasks = [...current.filter((t) => !t.isGoal), ...kept.filter((t) => !t.isGoal), ...carried]
    .filter((t, i, all) => all.findIndex((x) => x.id === t.id) === i)
  // The Save step's "Already in your plan" is what is SAVED — never a Keep
  // the reader has only proposed. Listing kept rows there put the same row
  // under "already in" and "what Save will change" at once (walkthrough,
  // 2026-09-25). The Plan step still shows the draft's whole list.
  const savedGoals = week ? [] : current.filter((t) => t.isGoal)
  const savedTasks = current.filter((t) => !t.isGoal)
  // A task toward a goal belongs to the goal's domain; a loose one to the domain in view.
  // On a week the goals are the MONTH's (the rung above): a new week task
  // written toward one is that goal's task, with the goal kept whole
  // on its month (horizon flows).
  const towardOptions = week
    ? aboveGoals.map((g) => ({ id: g.id, title: g.title, context: g.context ?? null }))
    : [
      ...monthGoals.map((g) => ({ id: g.id, title: g.title, context: g.context ?? null })),
      ...draft.newGoals.map((g) => ({ id: g.id, title: g.title, context: g.context ?? null })),
    ]
  // Is there anything for Save to write? A reflection note counts: it is
  // saved on the session record even when no row changed.
  const nothingToSave = useMemo(() => {
    const d = draft
    return Object.keys(d.verdicts).length === 0
      && d.newGoals.length === 0 && d.newTasks.length === 0 && d.takenFromAbove.length === 0
      && !d.wentWell.trim() && !d.didnt.trim()
  }, [draft])

  /**
   * The saved plan, drawn the way Month draws it: each step under its goal,
   * completed work kept and marked. Two flat lists put a step nowhere near the
   * goal it serves and hid what was already finished (Codex, 2026-09-24).
   *
   * The filter is presentation ONLY — the draft, and so everything Save
   * writes, is untouched by it, and the line beneath it says so.
   */
  const [planFilter, setPlanFilter] = useState('')
  const [revealed, setRevealed] = useState<Set<string>>(new Set())
  const [openGoals, setOpenGoals] = useState<Set<string>>(new Set())
  const existing = useMemo(() => planRowsFor(monthGoals, monthTasks), [monthGoals, monthTasks])
  const existingView = useMemo(() => goalListView(existing.goals, existing.loose, {
    query: planFilter, inReview: true, expanded: openGoals, revealed,
  }), [existing, planFilter, openGoals, revealed])
  const existingHidden = hiddenLabel(existingView)
  const manyRows = existing.goals.length + existing.loose.length > 8

  const lines = useMemo(() => summarize(draft, { open, above, aboveGoals, current, hiddenStepGoals, periodLabel: P, prevLabel: Q, aboveLabel }), [draft, open, above, aboveGoals, current, hiddenStepGoals, P, Q, aboveLabel])
  // What Save writes, and what it leaves alone. A row from the look-back with
  // no verdict is never written, and showing it under a "will change" heading
  // contradicted the sentence saying nothing would be (Codex, 2026-09-24).
  const changing = useMemo(() => lines.filter((l) => !l.unchanged), [lines])
  const untouched = useMemo(() => lines.filter((l) => l.unchanged), [lines])

  /** "+ Add to <week>" for one row of the level above, or its marker once taken. */
  const aboveAdd = (a: Task) => (step === 'plan' ? (draft.takenFromAbove.includes(a.id)
    ? <span className="ml-1 text-[12px] font-semibold text-sage-600">{copy.marker}</span>
    : <button type="button" aria-label={`Add to ${P}: ${a.title}`} className="block text-[12px] font-semibold text-primary-700"
        onClick={() => set({ takenFromAbove: [...draft.takenFromAbove, a.id] })}>+ Add to {P}</button>) : null)

  const steps: Array<[Step, string]> = [['back', `Look back at ${Q}`], ['plan', `Plan ${P}`], ['save', 'Save']]
  const idx = steps.findIndex(([s]) => s === step)

  return (
    <div className={`grid grid-cols-1 items-start gap-5 ${year ? 'lg:grid-cols-1' : 'lg:grid-cols-[minmax(0,1fr)_320px]'}`}>
      <div className="min-w-0">
        <ol className="mb-4 flex overflow-hidden rounded-lg border border-neutral-200 text-[13px] font-semibold" aria-label="Planning steps">
          {steps.map(([s, label], i) => (
            <li key={s} aria-current={i === idx ? 'step' : undefined}
              className={`flex-1 px-2 py-2 text-center ${i === idx ? 'bg-neutral-900 text-white' : i < idx ? 'bg-sage-50 text-sage-600' : 'bg-neutral-50 text-neutral-400'}`}>
              {s === 'back' && nothingBack ? <span className="font-normal italic">Nothing to look back at</span> : <>{i < idx && '✓ '}{label}</>}
            </li>
          ))}
        </ol>

        {step === 'back' && (
          <section>
            <h2 className="font-display text-xl text-neutral-800">How did {Q} go?</h2>
            <p className="mt-1 text-sm text-neutral-500">{copy.backSub}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="text-[13px] font-semibold text-neutral-700">What went well?
                <textarea className="input-base mt-1 min-h-[56px]" value={draft.wentWell} onChange={(e) => set({ wentWell: e.target.value })} />
              </label>
              <label className="text-[13px] font-semibold text-neutral-700">What didn't?
                <textarea className="input-base mt-1 min-h-[56px]" value={draft.didnt} onChange={(e) => set({ didnt: e.target.value })} />
              </label>
            </div>
            <p className="mt-1 text-[12px] text-neutral-400">Visible to your household.</p>
            {finished.length > 0 && (
              <>
                <h3 className="mt-5 border-b border-neutral-200 pb-1 font-display text-lg text-neutral-800">{copy.finished}</h3>
                <ul>{finished.map((t) => (
                  <li key={t.id} className="flex items-center gap-2 border-b border-neutral-100 py-2 text-sm text-neutral-600">
                    {t.isGoal ? <Target className="h-4 w-4 text-accent-600" /> : <Check className="h-4 w-4 text-sage-600" />}{t.title}
                  </li>))}</ul>
              </>
            )}
            {open.length > 0 && (
              <>
                <h3 className="mt-5 border-b border-neutral-200 pb-1 font-display text-lg text-neutral-800">Still open</h3>
                <ul>{open.map((t) => (
                  <li key={t.id} className="border-b border-neutral-100 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {t.isGoal && <Target className="h-4 w-4 text-accent-600" />}
                      <span className="min-w-[180px] flex-1 text-sm text-neutral-800">{t.title}</span>
                      <div className="flex flex-wrap gap-1">
                        {verdictOptions(!!t.isGoal, level).map((o) => (
                          <button key={o.verdict} type="button" aria-pressed={draft.verdicts[t.id] === o.verdict}
                            onClick={() => setVerdict(t.id, o.verdict)}
                            className={`rounded-md border px-2 py-1 text-[12.5px] font-semibold ${draft.verdicts[t.id] === o.verdict ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-200 bg-white text-neutral-700'}`}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {draft.verdicts[t.id] === 'keep-action' && (
                      <label className="mt-2 block pl-6 text-[12.5px] text-neutral-500">Task for {t.title}
                        <div className="mt-1">
                          <input className="input-base" value={draft.actionTitles[t.id] ?? ''}
                            aria-invalid={askForActions && unnamedActions.includes(t.id) ? true : undefined}
                            onChange={(e) => set({ actionTitles: { ...draft.actionTitles, [t.id]: e.target.value },
                              actionIds: { ...draft.actionIds, [t.id]: draft.actionIds[t.id] ?? newId() } })} />
                        </div>
                      </label>
                    )}
                    {askForActions && unnamedActions.includes(t.id) && (
                      <p role="alert" className="mt-1 pl-6 text-[12.5px] font-semibold text-accent-700">Name the task, or choose Keep</p>
                    )}
                  </li>))}</ul>
              </>
            )}
          </section>
        )}

        {step === 'plan' && (
          <section>
            <h2 className="font-display text-xl text-neutral-800">{copy.planHead}</h2>
            <p className="mt-1 text-sm text-neutral-500">{copy.planSub}</p>
            {!week && (<>
            <h3 className="mt-4 border-b-2 border-primary-700 pb-1 font-display text-lg text-neutral-800">{P} goals</h3>
            {(level === 'month' || level === 'season') && (
              <div className="mt-2">
                <Hint name="month-goals" uid={uid}>Goals are what this period should add up to. They stay on this list; you look at them when you plan a week or a day.</Hint>
              </div>
            )}
            {(manyRows || planFilter) && (
              <div className="period-goals-tools mt-2">
                <input type="search" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                  onKeyDown={clearFilterOnEscape(planFilter, () => setPlanFilter(''))}
                  aria-label={`Filter your ${P} plan`} placeholder="Filter this plan…" className="period-goals-filter" />
              </div>
            )}
            {existingHidden && (
              <p role="status" className="period-goals-hidden">
                {existingHidden}. Filtering does not change what will be saved.
              </p>
            )}
            <ul>
              {existingView.goals.map((g) => (
                <li key={g.row.id} className="border-b border-neutral-100 py-2 text-sm">
                  <span className="flex items-center gap-2">
                    <Target className="h-4 w-4 shrink-0 text-accent-600" />
                    <span className={`min-w-0 flex-1 break-words ${g.row.fate === 'done' ? 'text-neutral-400 line-through' : ''}`}>{g.row.title}</span>
                    {g.counts.total > 0 && (
                      <button type="button" onClick={() => setOpenGoals((prev) => { const n = new Set(prev); if (n.has(g.row.id)) n.delete(g.row.id); else n.add(g.row.id); return n })}
                        className="shrink-0 text-[12px] text-primary-700 hover:underline">
                        {countsLabel(g.counts)}{g.expanded ? ' · hide' : ' · show'}
                      </button>
                    )}
                  </span>
                  {g.expanded && (
                    <ul className="mt-1 ml-6 border-l border-neutral-200 pl-3">
                      {g.steps.map((st) => (
                        <li key={st.id} className={`break-words py-1 text-[13px] ${st.fate === 'done' ? 'text-neutral-400 line-through' : 'text-neutral-600'}`}>
                          {st.title}{st.fate === 'done' && <span className="ml-1.5 text-[11px] no-underline">completed</span>}
                        </li>
                      ))}
                      {g.hiddenByReveal > 0 && (
                        <li className="py-1">
                          <button type="button" onClick={() => setRevealed((prev) => new Set(prev).add(g.row.id))}
                            className="text-[12px] font-medium text-primary-700 hover:underline">
                            Show all {g.counts.total} steps · {g.hiddenByReveal} more
                          </button>
                        </li>
                      )}
                      {g.hiddenByFilter > 0 && <li className="py-1 text-[11px] text-neutral-400">{g.hiddenByFilter} hidden by the filter</li>}
                    </ul>
                  )}
                </li>
              ))}
              {draft.newGoals.map((g) => (
                <li key={g.id} className="flex items-center gap-2 border-b border-neutral-100 py-2 text-sm">
                  <Target className="h-4 w-4 text-accent-600" />
                  <span className="flex-1">{g.title}{g.linkId && <span className="block text-[12px] text-neutral-400">for {aboveGoals.find((a) => a.id === g.linkId)?.title}</span>}</span>
                  <button type="button" className="text-[12px] text-primary-700" onClick={() => set({ newGoals: draft.newGoals.filter((x) => x.id !== g.id), newTasks: draft.newTasks.map((x) => (x.linkId === g.id ? { ...x, linkId: undefined } : x)) })}>Remove</button>
                </li>))}
            </ul>
            <form className="mt-2 flex flex-wrap gap-2" onSubmit={(e) => {
              e.preventDefault()
              const title = goalText.trim()
              if (!title || [...monthGoals.map((g) => g.title), ...draft.newGoals.map((g) => g.title)].some((x) => x.trim().toLowerCase() === title.toLowerCase())) return
              set({ newGoals: [...draft.newGoals, { id: newId(), title, linkId: goalFor || undefined, context: domainInView }] }); setGoalText(''); setGoalFor('')
            }}>
              <div className="min-w-[200px] flex-1"><input aria-label={`New goal for ${P}`} className="input-base" value={goalText} onChange={(e) => setGoalText(e.target.value)} placeholder={`A goal for ${P}`} /></div>
              {aboveGoals.length > 0 && (
                <select aria-label={`For a ${copy.goalForWord} goal`} className="rounded-md border border-neutral-200 px-2 text-sm" value={goalFor} onChange={(e) => setGoalFor(e.target.value)}>
                  <option value="">{`For a ${copy.goalForWord} goal? (optional)`}</option>
                  {aboveGoals.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
                </select>
              )}
              <button type="submit" className="rounded-md bg-primary-600 px-3 text-sm font-semibold text-white">Add goal</button>
            </form>
            </>)}

            {!year && (<>
            <h3 className={`mt-5 border-b border-neutral-300 pb-1 font-display ${week ? 'text-lg text-neutral-800' : 'text-base text-neutral-700'}`}>{copy.taskHead}{!week && <span className="ml-1.5 font-sans text-[12px] text-neutral-400">optional</span>}</h3>
            {week && (
              <div className="mt-2">
                <Hint name="week-list" uid={uid}>Adding a month task here puts it on this week's list too. The month keeps it and shows "on this week".</Hint>
              </div>
            )}
            <ul>
              {/* Only what belongs to no goal — the rest is drawn under its
                  goal above, where it can be read together. */}
              {existingView.loose.map((x) => (
                <li key={x.id} className={`break-words border-b border-neutral-100 py-2 text-sm ${x.fate === 'done' ? 'text-neutral-400 line-through' : ''}`}>
                  {x.title}{x.fate === 'done' && <span className="ml-1.5 text-[11px] no-underline">completed</span>}
                </li>
              ))}
              {open.filter((t) => draft.verdicts[t.id] === 'keep-action' && draft.actionTitles[t.id]?.trim()).map((g) => (
                <li key={`a-${g.id}`} className="border-b border-neutral-100 py-2 text-sm">{draft.actionTitles[g.id]}<span className="block text-[12px] text-neutral-400">new task toward {g.title}</span></li>))}
              {draft.newTasks.map((x) => (
                <li key={x.id} className="flex items-center gap-2 border-b border-neutral-100 py-2 text-sm">
                  <span className="flex-1">{x.title}{x.linkId && <span className="block text-[12px] text-neutral-400">toward {towardOptions.find((o) => o.id === x.linkId)?.title}</span>}{x.day && <span className="block text-[12px] text-neutral-400">on {dayOptions.find((o) => o.ymd === x.day)?.label ?? x.day}</span>}</span>
                  <button type="button" className="text-[12px] text-primary-700" onClick={() => set({ newTasks: draft.newTasks.filter((y) => y.id !== x.id) })}>Remove</button>
                </li>))}
              {above.filter((a) => draft.takenFromAbove.includes(a.id)).map((a) => (
                <li key={`t-${a.id}`} className="flex items-center gap-2 border-b border-neutral-100 py-2 text-sm">
                  <span className="flex-1">{a.title}<span className="block text-[12px] text-neutral-400">from {aboveLabel}</span></span>
                  <button type="button" className="text-[12px] text-primary-700" onClick={() => set({ takenFromAbove: draft.takenFromAbove.filter((id) => id !== a.id) })}>Remove</button>
                </li>))}
            </ul>
            {!week && !addingTask && draft.newTasks.length === 0 ? (
              <button type="button" onClick={() => setAddingTask(true)} className="mt-2 text-[13px] text-neutral-500 hover:text-primary-700">
                + Add a task
              </button>
            ) : (
            <form className="mt-2 flex flex-wrap gap-2" onSubmit={(e) => {
              e.preventDefault()
              const title = taskText.trim()
              if (!title) return
              const toward = towardOptions.find((o) => o.id === taskToward)
              set({ newTasks: [...draft.newTasks, { id: newId(), title, linkId: toward?.id, day: taskDay || undefined, context: toward ? toward.context : domainInView }] }); setTaskText(''); setTaskToward(''); setTaskDay('')
            }}>
              <div className="min-w-[200px] flex-1"><input aria-label={`New task for ${P}`} className="input-base" value={taskText} onChange={(e) => setTaskText(e.target.value)} placeholder={`A task for ${P}`} /></div>
              {week && (
                <select aria-label="Day for this task" className="rounded-md border border-neutral-200 px-2 text-sm" value={taskDay} onChange={(e) => setTaskDay(e.target.value)}>
                  <option value="">Any day</option>
                  {dayOptions.map((o) => <option key={o.ymd} value={o.ymd}>{o.label}</option>)}
                </select>
              )}
              {towardOptions.length > 0 && (
                <select aria-label={week ? `Toward a goal for ${aboveLabel}` : `Toward a goal for ${P}`} className="rounded-md border border-neutral-200 px-2 text-sm" value={taskToward} onChange={(e) => setTaskToward(e.target.value)}>
                  <option value="">{week ? `Toward a goal for ${aboveLabel}? (optional)` : `Toward a goal for ${P}? (optional)`}</option>
                  {towardOptions.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
                </select>
              )}
              <button type="submit" className="rounded-md border border-neutral-200 px-3 text-sm font-semibold">Add task</button>
            </form>
            )}
            </>)}
          </section>
        )}

        {step === 'save' && (
          <section>
            <h2 className="font-display text-xl text-neutral-800">{copy.saveHead}</h2>
            {/* "Nothing is saved yet" was said over periods that already held
                a goal and three tasks, which read as "your work is gone"
                (S3-04). The sentence is about the CHANGES on this screen, and
                when there are none it says so instead (requirement 7). */}
            {saveError
              ? <p role="alert" className="mt-1 text-sm text-accent-700">Some of this didn't save. It's still here and in your draft; Save again retries only these.</p>
              : nothingToSave
                ? <p className="mt-1 text-sm text-neutral-600">Your plan for {P} is unchanged. Nothing will be written.</p>
                : <p className="mt-1 text-sm text-neutral-500">These changes are not saved yet.</p>}
            {needsDomain.length > 0 && <fieldset className="my-3 rounded border border-neutral-200 p-3"><legend className="text-sm">Choose where these items belong before saving</legend><p className="mb-2 text-xs text-neutral-500">Family is shared with your household. Personal and Work stay private.</p>{needsDomain.map(task => <label key={task.id} className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">{task.title}<select aria-label={`Domain for ${task.title}`} value={draft.domains?.[task.id] ?? ''} onChange={event => { const domains = { ...draft.domains }; if (event.target.value) domains[task.id] = event.target.value as DomainId; else delete domains[task.id]; set({ domains }) }} className="rounded border border-neutral-200 p-2"><option value="">Choose a domain</option>{DOMAINS.map(domain => <option key={domain.id} value={domain.id}>{domain.label}</option>)}</select></label>)}</fieldset>}

            {/* The existing plan, beside the proposed edits rather than
                replaced by them: a session must start from saved work and
                keep it visible while changes are being reviewed. Completed
                work is included — it is what this period got done. */}
            {(savedGoals.length > 0 || savedTasks.length > 0) && (
              <section aria-label={`Already in the plan for ${P}`} className="mt-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Already in the plan for {P}</h3>
                <ul className="mt-1">
                  {savedGoals.map((g) => (
                    <li key={g.id} className="flex items-center gap-2 border-b border-neutral-100 py-1.5 text-sm last:border-0">
                      <Target className="h-3.5 w-3.5 shrink-0 text-accent-600" aria-hidden="true" />
                      <span className={g.completed ? 'text-neutral-400 line-through' : 'text-neutral-700'}>{g.title}</span>
                    </li>))}
                  {savedTasks.map((x) => (
                    <li key={x.id} className="border-b border-neutral-100 py-1.5 text-sm last:border-0">
                      <span className={x.completed ? 'text-neutral-400 line-through' : 'text-neutral-700'}>{x.title}</span>
                      {x.completed && <span className="ml-1.5 text-[11px] text-neutral-400">completed</span>}
                    </li>))}
                </ul>
              </section>
            )}

            {changing.length > 0 && (
              <>
                <h3 className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">What Save will change</h3>
                <ul aria-label="What Save will change" className="mt-1 rounded-lg bg-sage-50 px-4 py-2">
                  {changing.map((l, i) => (
                    <li key={i} className="grid grid-cols-1 gap-1 border-t border-sage-100 py-1.5 text-sm first:border-t-0 sm:grid-cols-2">
                      <span className="text-neutral-800">{l.title}</span><span className="text-neutral-600">→ {l.destination}</span>
                    </li>))}
                </ul>
              </>
            )}

            {/* The rows Save does not touch, kept as review context but never
                under a heading that says they change. */}
            {untouched.length > 0 && (
              <section aria-label={`Unchanged by Save`} className="mt-4">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
                  Save won&rsquo;t touch these
                </h3>
                <ul className="mt-1 px-1">
                  {untouched.map((l, i) => (
                    <li key={i} className="grid grid-cols-1 gap-1 border-b border-neutral-100 py-1.5 text-sm last:border-0 sm:grid-cols-2">
                      <span className="text-neutral-600">{l.title}</span>
                      <span className="text-neutral-500">{l.destination}</span>
                    </li>))}
                </ul>
              </section>
            )}
          </section>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          {step === 'back' || (step === 'plan' && nothingBack)
            ? <span />
            : <button type="button" className="rounded-md border border-neutral-200 px-3 py-1.5 text-sm" onClick={() => setStep(step === 'save' ? 'plan' : 'back')}>← Back</button>}
          <button type="button" disabled={saving} className="rounded-md border border-neutral-200 px-3 py-1.5 text-sm disabled:opacity-60" onClick={onClose}>Close · keep my draft</button>
          {step === 'back' && <button type="button" className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white" onClick={() => goTo('plan')}>Next: plan {P} →</button>}
          {step === 'plan' && <button type="button" className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white" onClick={() => goTo('save')}>Next: save →</button>}
          {/* An unchanged plan closes without a write. Offering "Save October"
              over nothing invited a pointless round-trip and implied the
              existing plan needed saving (requirement 7).

              …unless a save already failed. Every row can land and the SESSION
              RECORD still be refused, and then the draft holds no work, so
              this read "nothing will be written" and offered only Done — while
              the alert above it said "Save again retries only these". There
              was no Save to press, and the period stayed unplanned. Found by
              the review integration test, 2026-09-24. */}
          {step === 'save' && (nothingToSave && !saveError
            ? <button type="button" className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white" onClick={onClose}>Done</button>
            : <button type="button" disabled={saving || missingDomains.length > 0} className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60" onClick={() => { void onSave() }}>{saving ? 'Saving…' : `Save ${P}`}</button>)}
        </div>
      </div>

      {!year && (
        <aside className="min-w-0 rounded-xl bg-neutral-50 p-3 lg:sticky lg:top-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">{aboveLabel}</p>
          <ul className="mt-1">
            {aboveGoals.map((g) => (
              <li key={g.id} className="border-t border-neutral-200 py-1.5 text-[13px] text-neutral-700">
                <span className="flex items-center gap-1.5"><Target className="h-3.5 w-3.5 shrink-0 text-accent-600" />{g.title}</span>
                {/* On the week, the goal's own tasks sit under it — the
                    parent in view while its actions are chosen. */}
                {week && above.some((a) => a.goalTaskId === g.id) && (
                  <ul className="ml-5 mt-0.5">
                    {above.filter((a) => a.goalTaskId === g.id).map((a) => (
                      <li key={a.id} className="py-1 text-[13px] text-neutral-700">{a.title}{aboveAdd(a)}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            {above.filter((a) => !(week && a.goalTaskId && aboveGoals.some((g) => g.id === a.goalTaskId))).map((a) => (
              <li key={a.id} className="border-t border-neutral-200 py-1.5 text-[13px] text-neutral-700">
                {a.title}
                {step === 'plan' && (draft.takenFromAbove.includes(a.id)
                  ? <span className="ml-1 text-[12px] font-semibold text-sage-600">{copy.marker}</span>
                  : <button type="button" aria-label={`Add to ${P}: ${a.title}`} className="block text-[12px] font-semibold text-primary-700"
                      onClick={() => set({ takenFromAbove: [...draft.takenFromAbove, a.id] })}>+ Add to {P}</button>)}
              </li>))}
            {aboveGoals.length === 0 && above.length === 0 && <li className="py-1.5 text-[13px] text-neutral-400">{copy.aboveEmpty}</li>}
          </ul>
        </aside>
      )}
    </div>
  )
}
